import { useCallback, useEffect, useState } from 'react'
import { ApiClientError, type ApiKey, type CreatedApiKey, type Role, type User } from '../api/types.js'
import { IconAccess } from '../icons.js'
import { Alert } from '../ui/Alert.js'
import { CopyButton } from '../ui/CopyButton.js'
import { ConfirmDialog, useConfirmation } from '../ui/ConfirmDialog.js'
import { вКавычках } from '../ui/quotes.js'
import { useToaster } from '../ui/toasts.js'

/**
 * Узкий срез клиента: экрану нужны только пользователи, роли, каталог прав
 * и API-ключи (барьер 2 — клиент API есть граница процесса для экранов
 * админки, его двойник в тестах разрешён, см. Instances.tsx). Ровно те же
 * десять методов, что перечислены в задании задачи 14, плюс `enableUser` —
 * без него отключение из портала необратимо (FR-ADM-011).
 */
export interface AccessClient {
  users(): Promise<User[]>
  createUser(email: string, password: string): Promise<User>
  disableUser(id: string): Promise<void>
  enableUser(id: string): Promise<void>
  roles(): Promise<Role[]>
  createRole(name: string, permissions: string[]): Promise<Role>
  permissions(): Promise<string[]>
  apiKeys(): Promise<ApiKey[]>
  createApiKey(name: string, scopes: string[]): Promise<CreatedApiKey>
  revokeApiKey(id: string): Promise<void>
}

export interface AccessProps {
  client: AccessClient
  /** Обратившийся администратор — от него скрывается собственная отключаемость (FR-ADM-010). */
  me: { id: string }
  /** Права, выданные при входе (session.permissions()) — экран сам их не запрашивает. */
  permissions: string[]
}

const CAN_WRITE_USERS = 'users.write'
const CAN_WRITE_ROLES = 'roles.write'
const CAN_WRITE_API_KEYS = 'api-keys.write'

/** Сообщение по умолчанию для отказов не от сервера (например, обрыв сети). */
const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

function messageOf(cause: unknown): string {
  return cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE
}

export function Access(props: AccessProps) {
  const { client, me, permissions } = props
  const canWriteUsers = permissions.includes(CAN_WRITE_USERS)
  const canWriteRoles = permissions.includes(CAN_WRITE_ROLES)
  const canWriteApiKeys = permissions.includes(CAN_WRITE_API_KEYS)
  const toaster = useToaster()
  const отключение = useConfirmation<User>()
  const отзыв = useConfirmation<ApiKey>()

  const [error, setError] = useState<string | null>(null)

  // ---- Пользователи ----
  const [users, setUsers] = useState<User[] | null>(null)
  const [busyUsers, setBusyUsers] = useState<ReadonlySet<string>>(new Set())
  const [showUserForm, setShowUserForm] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [creatingUser, setCreatingUser] = useState(false)

  const loadUsers = useCallback(async () => {
    try {
      setUsers(await client.users())
    } catch (cause) {
      setError(messageOf(cause))
    }
  }, [client])

  // ---- Роли ----
  const [roles, setRoles] = useState<Role[] | null>(null)
  const [showRoleForm, setShowRoleForm] = useState(false)
  const [roleName, setRoleName] = useState('')
  const [selectedPermissions, setSelectedPermissions] = useState<ReadonlySet<string>>(new Set())
  const [creatingRole, setCreatingRole] = useState(false)

  const loadRoles = useCallback(async () => {
    try {
      setRoles(await client.roles())
    } catch (cause) {
      setError(messageOf(cause))
    }
  }, [client])

  // Каталог прав живёт своей загрузкой, отдельно от списка ролей: форма
  // создания роли обязана предлагать то, что реально можно выдать на
  // сервере, а не то, что уже кому-то назначено (FR-ADM-009).
  const [permissionsCatalog, setPermissionsCatalog] = useState<string[] | null>(null)

  const loadPermissionsCatalog = useCallback(async () => {
    try {
      setPermissionsCatalog(await client.permissions())
    } catch (cause) {
      setError(messageOf(cause))
    }
  }, [client])

  // ---- API-ключи ----
  const [apiKeys, setApiKeys] = useState<ApiKey[] | null>(null)
  const [busyApiKeys, setBusyApiKeys] = useState<ReadonlySet<string>>(new Set())
  const [showKeyForm, setShowKeyForm] = useState(false)
  const [keyName, setKeyName] = useState('')
  const [creatingKey, setCreatingKey] = useState(false)
  // Полный ключ — только из ответа на создание, тот же приём, что у ключа
  // подписи вебхука (Webhooks.tsx): список ключей его никогда не содержит
  // (FR-ADM-012), а показать его можно только один раз (FR-SEC-002).
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null)

  const loadApiKeys = useCallback(async () => {
    try {
      setApiKeys(await client.apiKeys())
    } catch (cause) {
      setError(messageOf(cause))
    }
  }, [client])

  useEffect(() => {
    void loadUsers()
    void loadRoles()
    void loadPermissionsCatalog()
    void loadApiKeys()
  }, [loadUsers, loadRoles, loadPermissionsCatalog, loadApiKeys])

  async function handleCreateUser(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (creatingUser || newEmail.trim() === '' || newPassword === '') return

    setCreatingUser(true)
    setError(null)
    try {
      const адрес = newEmail.trim()
      await client.createUser(адрес, newPassword)
      setNewEmail('')
      setNewPassword('')
      setShowUserForm(false)
      await loadUsers()
      toaster.succeeded(`Пользователь ${адрес} создан`)
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setCreatingUser(false)
    }
  }

  async function toggleUser(user: User) {
    if (busyUsers.has(user.id)) return
    отключение.cancel()
    setBusyUsers((current) => new Set(current).add(user.id))
    setError(null)
    try {
      if (user.disabledAt === null) {
        await client.disableUser(user.id)
      } else {
        await client.enableUser(user.id)
      }
      await loadUsers()
      toaster.succeeded(
        user.disabledAt === null
          ? `Пользователь ${user.email} отключён`
          : `Пользователь ${user.email} включён`,
      )
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setBusyUsers((current) => {
        const next = new Set(current)
        next.delete(user.id)
        return next
      })
    }
  }

  function togglePermission(permission: string) {
    setSelectedPermissions((current) => {
      const next = new Set(current)
      if (next.has(permission)) next.delete(permission)
      else next.add(permission)
      return next
    })
  }

  async function handleCreateRole(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (creatingRole || roleName.trim() === '') return

    setCreatingRole(true)
    setError(null)
    try {
      const название = roleName.trim()
      await client.createRole(название, [...selectedPermissions])
      setRoleName('')
      setSelectedPermissions(new Set())
      setShowRoleForm(false)
      await loadRoles()
      toaster.succeeded(`Роль ${вКавычках(название)} создана`)
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setCreatingRole(false)
    }
  }

  async function handleCreateKey(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (creatingKey || keyName.trim() === '') return

    setCreatingKey(true)
    setError(null)
    try {
      /*
       * Ключ выпускается с полным списком прав организации — и это список,
       * а не пустота (FR-ADM-041).
       *
       * До этой правки форма звала `createApiKey(имя, [])` в уверенности,
       * что пустой список скоупов означает «без ограничений». Сервер
       * считает ровно наоборот: `assertScope` требует, чтобы нужный скоуп
       * был в списке ключа, — а в пустом нет ни одного, то есть запрещено
       * всё. Каждый выпущенный из портала ключ отвечал 403 на любой запрос,
       * и ни одна строка на экране об этом не говорила. Нашлось живой
       * проверкой интеграции с 1С: ключ выпущен там, где сказано, и мёртв.
       *
       * Правило «пустой значит всё» не заводится и здесь: однажды по нему
       * выпустят всемогущий ключ по недосмотру, а не по решению. Явный
       * список к тому же виден в карточке ключа — права ключа перестают
       * быть тайной его выпуска.
       *
       * Каталог берётся свежим, если он не загрузился при открытии экрана:
       * выпустить ключ с пустым списком «потому что каталог не пришёл»
       * значит вернуть ту же беду через чёрный ход.
       */
      const права = permissionsCatalog ?? (await client.permissions())
      if (права.length === 0) {
        setError(
          'Платформа не назвала ни одного права, и ключ вышел бы мёртвым: он отвечал бы отказом ' +
            'на любой запрос. Обновите страницу и попробуйте ещё раз.',
        )
        return
      }

      const created = await client.createApiKey(keyName.trim(), права)
      setCreatedKey(created)
      setKeyName('')
      setShowKeyForm(false)
      await loadApiKeys()
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setCreatingKey(false)
    }
  }

  async function handleRevokeKey(key: ApiKey) {
    const id = key.id
    if (busyApiKeys.has(id)) return
    отзыв.cancel()
    setBusyApiKeys((current) => new Set(current).add(id))
    setError(null)
    try {
      await client.revokeApiKey(id)
      await loadApiKeys()
      toaster.succeeded(`Ключ ${вКавычках(key.name)} отозван`)
    } catch (cause) {
      setError(messageOf(cause))
    } finally {
      setBusyApiKeys((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }

  return (
    <div>
      {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      <section aria-label="Пользователи" className="card">
        <div className="card__title">
          <h2>Пользователи</h2>
          {canWriteUsers && (
            <button type="button" className="btn btn--primary" onClick={() => setShowUserForm((value) => !value)}>
              Создать пользователя
            </button>
          )}
        </div>

        {canWriteUsers && showUserForm && (
          <form className="form-inline" onSubmit={(event) => void handleCreateUser(event)}>
            <div className="field">
              <label htmlFor="new-user-email">Адрес электронной почты</label>
              <input
                id="new-user-email"
                type="email"
                value={newEmail}
                onChange={(event) => setNewEmail(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="new-user-password">Пароль</label>
              <input
                id="new-user-password"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </div>
            <button type="submit" className="btn btn--primary" disabled={creatingUser}>
              Создать
            </button>
          </form>
        )}

        {users === null ? (
          <p>Загрузка пользователей…</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Эл. почта</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const busy = busyUsers.has(user.id)
                // Собственная строка не предлагает отключение ни при каких
                // правах: включить себя обратно, отключив доступ к порталу,
                // было бы некому (FR-ADM-010).
                const canToggle = canWriteUsers && user.id !== me.id
                return (
                  <tr key={user.id}>
                    <td>{user.email}</td>
                    <td>
                      <span className={`badge badge--${user.disabledAt !== null ? 'danger' : 'ok'}`}>
                        {user.disabledAt !== null ? 'Отключён' : 'Активен'}
                      </span>
                    </td>
                    <td>
                      {canToggle &&
                        (user.disabledAt === null ? (
                          <button type="button" className="btn btn--small" disabled={busy} onClick={() => отключение.ask(user)}>
                            Отключить
                          </button>
                        ) : (
                          <button type="button" className="btn btn--small" disabled={busy} onClick={() => void toggleUser(user)}>
                            Включить
                          </button>
                        ))}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </section>

      <section aria-label="Роли" className="card">
        <div className="card__title">
          <h2>Роли</h2>
          {canWriteRoles && (
            <button type="button" className="btn btn--primary" onClick={() => setShowRoleForm((value) => !value)}>
              Создать роль
            </button>
          )}
        </div>

        {canWriteRoles && showRoleForm && (
          <form onSubmit={(event) => void handleCreateRole(event)}>
            <div className="field">
              <label htmlFor="new-role-name">Название роли</label>
              <input
                id="new-role-name"
                value={roleName}
                onChange={(event) => setRoleName(event.target.value)}
              />
            </div>
            <div className="field">
              {(permissionsCatalog ?? []).map((permission) => (
                <span key={permission} style={{ display: 'block' }}>
                  <input
                    type="checkbox"
                    id={`role-permission-${permission}`}
                    checked={selectedPermissions.has(permission)}
                    onChange={() => togglePermission(permission)}
                  />{' '}
                  <label htmlFor={`role-permission-${permission}`} style={{ display: 'inline' }}>
                    {permission}
                  </label>
                </span>
              ))}
            </div>
            <button type="submit" className="btn btn--primary" disabled={creatingRole}>
              Создать
            </button>
          </form>
        )}

        {roles === null ? (
          <p>Загрузка ролей…</p>
        ) : roles.length === 0 ? (
          <div className="empty-state">
            <IconAccess />
            <p>Ролей пока нет — создайте первую, чтобы можно было выдавать права не по одному.</p>
          </div>
        ) : (
          <ul className="entity-list">
            {roles.map((role) => (
              <li key={role.id} className="entity-row">
                {role.name} — {role.permissions.length > 0 ? role.permissions.join(', ') : 'без прав'}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="API-ключи" className="card">
        <div className="card__title">
          <h2>API-ключи</h2>
          {canWriteApiKeys && (
            <button type="button" className="btn btn--primary" onClick={() => setShowKeyForm((value) => !value)}>
              Создать ключ
            </button>
          )}
        </div>

        {createdKey !== null && (
          <div className="secret-panel">
            <p>
              Полный ключ: <code>{createdKey.key}</code>
            </p>
            <p role="alert">
              Скопируйте ключ сейчас — он больше не будет показан. Сервер хранит только его
              хэш и не может показать его снова; при утере ключ можно получить только
              созданием нового.
            </p>
            <div className="btn-row">
              {/* Раз просим скопировать — даём чем (FR-ADM-036). */}
              <CopyButton value={createdKey.key} what="Ключ" label="Скопировать ключ" />
              <button type="button" className="btn" onClick={() => setCreatedKey(null)}>
                Ключ сохранён, закрыть
              </button>
            </div>
          </div>
        )}

        {canWriteApiKeys && showKeyForm && (
          <form onSubmit={(event) => void handleCreateKey(event)}>
            <div className="field">
              <label htmlFor="new-key-name">Название</label>
              <input
                id="new-key-name"
                value={keyName}
                onChange={(event) => setKeyName(event.target.value)}
              />
              {/* Права ключа названы до его выпуска, а не после: ключ,
                  который может всё, выпускают осознанно (FR-ADM-041). */}
              <p className="поле__подсказка">
                Ключ получает все права организации — ровно то, что умеет портал. Ограничить
                его отдельными правами пока нельзя: когда ключ станет не нужен, его отзывают.
              </p>
            </div>
            <button type="submit" className="btn btn--primary" disabled={creatingKey}>
              Создать
            </button>
          </form>
        )}

        {apiKeys === null ? (
          <p>Загрузка ключей…</p>
        ) : apiKeys.length === 0 ? (
          <div className="empty-state">
            <IconAccess />
            <p>Ключей пока нет — создайте первый, чтобы подключить внешнюю систему (например, 1С).</p>
          </div>
        ) : (
          // aria-label обособляет список от баннера с полным ключом рядом
          // (оба в одной секции «API-ключи»): тест на утечку полного
          // значения в список обязан смотреть именно сюда, а не туда, где
          // баннер его законно показывает один раз (FR-SEC-002).
          <ul aria-label="Список ключей" className="entity-list">
            {apiKeys.map((key) => {
              const busy = busyApiKeys.has(key.id)
              return (
                <li key={key.id} className="entity-row">
                  {/* Имя ключа — в своём узле: иначе у RTL нет элемента, чей
                      textContent точно равен одному имени, а не имени вместе
                      с префиксом и действиями по соседству. */}
                  <span>{key.name}</span> — {key.prefix}…{key.revokedAt !== null ? ' (отозван)' : ''}
                  {/* Права ключа видны в его карточке (FR-ADM-041): ключ,
                      который не работает, иначе неотличим от рабочего — ни
                      здесь, ни где-либо ещё в портале. */}
                  <span className="entity-row__meta">
                    {key.scopes.length === 0
                      ? 'Прав нет: ключ отвечает отказом на любой запрос'
                      : `Прав: ${key.scopes.length}`}
                  </span>
                  {canWriteApiKeys && key.revokedAt === null && (
                    <button
                      type="button"
                      className="btn btn--small btn--danger"
                      disabled={busy}
                      onClick={() => отзыв.ask(key)}
                    >
                      Отозвать
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {отключение.pending !== null && (
        <ConfirmDialog
          title={`Отключить ${отключение.pending.email}?`}
          confirmLabel="Отключить"
          busy={busyUsers.has(отключение.pending.id)}
          onCancel={отключение.cancel}
          onConfirm={() => {
            if (отключение.pending !== null) void toggleUser(отключение.pending)
          }}
        >
          <p>
            Человек перестанет входить в портал сразу же; открытая у него вкладка перестанет
            работать при следующем действии.
          </p>
          <p>Включить его обратно можно здесь же — учётная запись и её права сохранятся.</p>
        </ConfirmDialog>
      )}

      {отзыв.pending !== null && (
        <ConfirmDialog
          title={`Отозвать ключ ${вКавычках(отзыв.pending.name)}?`}
          confirmLabel="Отозвать"
          busy={busyApiKeys.has(отзыв.pending.id)}
          onCancel={отзыв.cancel}
          onConfirm={() => {
            if (отзыв.pending !== null) void handleRevokeKey(отзыв.pending)
          }}
        >
          <p>
            Внешняя система, которая обращается к API с этим ключом (например, 1С), перестанет
            получать ответы: её запросы начнут отклоняться сразу после отзыва.
          </p>
          <p>Отозванный ключ вернуть нельзя — на замену придётся выдать новый.</p>
        </ConfirmDialog>
      )}
    </div>
  )
}
